import DateSelector, { type DateSelectorProps } from '@/components/selectors/DateSelector.tsx';
import { FormHelperText } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import useFormField from '@core/hooks/useFormField.ts';

type ValidationRule = (value: any) => string;

export type FormDateSelectorProps = DateSelectorProps & {
    name: string;
    rules?: ValidationRule[];
    validateOn?: 'blur' | 'change' | 'both';
    helperText?: string;
};

const FormDateSelector: React.FC<FormDateSelectorProps> = (props) => {
    const { name, rules = [], label, validateOn = 'both', helperText: staticHelperText, ...selectorProps } = props;
    const { value, onChange } = props;

    const { error, showError, handleBlur, handleChange } = useFormField({
        name,
        label,
        value,
        rules,
        validateOn,
        onChange
    });

    // Compare by content: arrays get a new reference on every change
    const valueKey = Array.isArray(value) ? value.join(',') : value;
    const isFirstRender = useRef(true);
    const prevKey = useRef(valueKey);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            prevKey.current = valueKey;
            return;
        }
        if (prevKey.current === valueKey) return;
        prevKey.current = valueKey;
        handleBlur();
    }, [valueKey]);

    const dateSelectorProps = {
        ...selectorProps,
        label: label,
        onChange: handleChange
    } as DateSelectorProps;

    return (
        <>
            <DateSelector {...dateSelectorProps} hasError={showError} />
            {showError && <FormHelperText className={`selector-helper error`}>{error}</FormHelperText>}
            {!showError && staticHelperText && <FormHelperText>{staticHelperText}</FormHelperText>}
        </>
    );
};

FormDateSelector.displayName = 'FormDateSelector';

export default FormDateSelector;
